import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, StateView, useAuth, useToast } from "@ozpanel/ui";

// PostgreSQL: replication, databases, and what is running right now.
//
// The module mounts 36 routes and v2 reached one of them. Everything an operator does with
// Postgres on this platform — see who is primary, promote, switch over, rejoin a standby after a
// failure, create a database, add a user, cancel a query that is holding a lock — had no screen.
//
// The cluster half is repmgr, and its actions are the dangerous ones on this page: promote and
// switchover change which node accepts writes. Each says what it does to the OTHER node, because
// that is the part an operator cannot see from here and the part that loses data if it is wrong.

interface PgDatabase {
  name: string;
  owner?: string;
  encoding?: string;
  collation?: string;
  connectable?: boolean;
  orphan?: boolean;
  size_bytes?: number;
  system?: boolean;
  // A platform database: the module that owns it, what is lost without it, and whether it is in use or a
  // kept standby (an engine that can be switched back) — never a leftover to tidy away.
  platform?: boolean;
  module?: string;
  purpose?: string;
  use?: string;
}
interface PgActivity {
  pid: number;
  db?: string;
  user?: string;
  client?: string;
  state?: string;
  seconds?: number;
  query?: string;
  wait?: string;
}

export function PostgresOps() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [confirm, setConfirm] = React.useState<{ title: string; body: React.ReactNode; label: string; go: () => Promise<unknown> } | null>(null);
  const [newDb, setNewDb] = React.useState<{ name: string; owner: string } | null>(null);
  const [newUser, setNewUser] = React.useState<{ db: string; username: string; password: string } | null>(null);

  const cluster = useQuery({
    queryKey: ["pg-cluster"],
    queryFn: () => client.apiFetch<{ configured?: boolean; single_node?: boolean; this_node?: { role?: string }; total?: number }>("/postgresql/cluster"),
    retry: false,
  });
  const replication = useQuery({
    queryKey: ["pg-replication"],
    queryFn: () => client.apiFetch<{ role?: string; replicas_json?: string }>("/postgresql/replication"),
    retry: false,
  });
  const databases = useQuery({
    queryKey: ["pg-databases"],
    queryFn: () => client.apiFetch<{ items: PgDatabase[] | null; orphans?: number; total?: number }>("/postgresql/databases"),
    retry: false,
  });
  const activity = useQuery({
    queryKey: ["pg-activity"],
    queryFn: () => client.apiFetch<{ items: PgActivity[] | null; total?: number }>("/postgresql/activity"),
    retry: false,
    refetchInterval: 10000,
  });

  const run = (label: string, p: Promise<unknown>) =>
    p
      .then(() => {
        notify({ kind: "success", title: label });
        for (const k of ["pg-cluster", "pg-replication", "pg-databases", "pg-activity"]) void qc.invalidateQueries({ queryKey: [k] });
      })
      .catch((e: unknown) => notify({ kind: "error", title: `${label} failed`, body: e instanceof Error ? e.message : String(e) }));

  const isPrimary = (replication.data?.role ?? cluster.data?.this_node?.role) === "primary";

  const dbCols: DataListColumn<PgDatabase>[] = [
    {
      key: "name", label: "Database", sortable: true,
      render: (d) => (
        <div>
          <span className="flex items-center gap-2">
            <span className="font-mono text-[12.5px]">{d.name}</span>
            {d.system && <StatusPill tone="neutral">system</StatusPill>}
            {d.platform && <StatusPill tone="accent">{d.module ? `${d.module}` : "platform"}</StatusPill>}
            {d.use && <StatusPill tone={d.use.startsWith("in use") ? "ok" : "neutral"}>{d.use.split(" — ")[0]}</StatusPill>}
          </span>
          {d.platform && (d.purpose || d.use) && (
            <div className="mt-0.5 max-w-xl text-[11.5px] text-muted">
              {d.purpose}
              {d.use && d.use.includes(" — ") ? <> · {d.use.split(" — ").slice(1).join(" — ")}</> : null}
            </div>
          )}
        </div>
      ),
    },
    { key: "owner", label: "Owner", sortable: true, render: (d) => d.owner || "—" },
    {
      // An ORPHAN is a database with no tenant behind it — storage nobody is billed for and
      // nobody will miss, but also the thing you must not drop without checking.
      key: "orphan", label: "", value: (d) => (d.orphan ? 1 : 0),
      render: (d) => (d.orphan ? <StatusPill tone="warn">no tenant</StatusPill> : null),
    },
    {
      key: "connectable", label: "", value: (d) => (d.connectable === false ? 1 : 0),
      render: (d) => (d.connectable === false ? <StatusPill tone="bad">not connectable</StatusPill> : null),
    },
  ];

  const actCols: DataListColumn<PgActivity>[] = [
    { key: "pid", label: "PID", sortable: true, render: (a) => <span className="font-mono text-[12px]">{a.pid}</span> },
    { key: "db", label: "Database", sortable: true, render: (a) => a.db || "—" },
    { key: "user", label: "User", sortable: true, render: (a) => a.user || "—" },
    {
      key: "seconds", label: "Running", sortable: true, value: (a) => a.seconds ?? 0,
      // A long-running query is the usual cause of "the site is slow" and of a lock nothing else
      // can get past, so the duration is what this table is sorted and coloured by.
      render: (a) => (
        <span className={(a.seconds ?? 0) > 60 ? "text-crit" : (a.seconds ?? 0) > 5 ? "text-warn" : "text-muted"}>
          {a.seconds != null ? `${Math.round(a.seconds)}s` : "—"}
        </span>
      ),
    },
    { key: "state", label: "State", sortable: true, render: (a) => <span className="text-[12px]">{a.state || "—"}{a.wait ? ` · ${a.wait}` : ""}</span> },
    {
      key: "query", label: "Query",
      render: (a) => <span className="line-clamp-2 max-w-[26rem] font-mono text-[11.5px] text-muted">{a.query || "—"}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      {/* No promote / rejoin / switchover buttons here. They called /postgresql/cluster/{promote,rejoin,
          switchover}, which are PLANNED routes that answer 501 — and a PostgreSQL-only role change would
          be a second actor beside the one chain that decides who is primary (cluster/pgchain.go, driven
          by the signed declaration). The real operator actions, each in the one place it lives: */}
      <Section title="Replication">
        <div className="mb-3 rounded border border-line px-3 py-2 text-[12px] leading-relaxed text-muted">
          Which node is the PostgreSQL primary follows the cluster's signed declaration, and a standby that falls
          behind or diverges is rejoined by the same chain automatically. To act on it by hand:{" "}
          <b className="text-fg">hand the workload over</b> under Cluster → Nodes → Cluster state (moves PostgreSQL,
          Redis and the arbitrator together); <b className="text-fg">promote a survivor</b> under Cluster → Recovery
          when the other nodes are gone; <b className="text-fg">rewind a diverged node</b> on the repmgrd module page,
          offered only while a second primary is measured.
        </div>
        <StateView loading={cluster.isLoading} error={cluster.error} errorContext="the Postgres cluster">
          {cluster.data?.configured === false ? (
            <div className="text-[13px] text-muted">
              Replication is not configured on this node — Postgres is running standalone.
            </div>
          ) : (
            <>
              <div className="mb-3 text-[13px]">
                This node is <StatusPill tone={isPrimary ? "accent" : "neutral"}>{replication.data?.role ?? cluster.data?.this_node?.role ?? "?"}</StatusPill>
                {cluster.data?.single_node && <span className="ml-2 text-[12px] text-warn">the only node — nothing is replicating</span>}
              </div>
              {/* The node table itself is rendered by PostgresCluster directly above this panel.
                  Repeating it here would put the same rows on the screen twice, which reads as two
                  different sources that might disagree. */}
            </>
          )}
        </StateView>
      </Section>

      <Section
        title="Databases"
        hint={databases.data?.total != null ? `${databases.data.total}` : undefined}
        bodyClassName="p-0"
        actions={<Button size="sm" onClick={() => setNewDb({ name: "", owner: "" })}>New database</Button>}
      >
        <div className="p-4">
          <DataList
            items={databases.data?.items ?? []}
            columns={dbCols}
            getRowKey={(d) => d.name}
            loading={databases.isLoading}
            error={databases.error}
            errorContext="the databases"
            emptyMessage="No databases."
            rowActions={(d) => (
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setNewUser({ db: d.name, username: "", password: "" })}>Add user</Button>
                {/* A system or platform database is never dropped from here — the server refuses it too. */}
                {!d.system && !d.platform && <Button size="sm" variant="danger"
                  onClick={() => setConfirm({
                    title: `Drop ${d.name}?`,
                    body: <p>Every table, row and grant in it is destroyed. There is no undo and no copy unless a backup exists.</p>,
                    label: "Drop it",
                    go: () => client.apiFetch(`/postgresql/databases/${encodeURIComponent(d.name)}`, { method: "DELETE" }),
                  })}>
                  Drop
                </Button>}
              </div>
            )}
          />
        </div>
      </Section>

      <Section title="Running now" hint={activity.data?.total != null ? `${activity.data.total}` : undefined} bodyClassName="p-0">
        <div className="px-4 pt-3 text-[12px] leading-relaxed text-muted">
          Every backend currently connected. Cancelling stops the QUERY and leaves the connection —
          the client sees an error and can retry, which is almost always what you want over killing
          the connection itself.
        </div>
        <div className="p-4">
          <DataList
            items={activity.data?.items ?? []}
            columns={actCols}
            getRowKey={(a) => String(a.pid)}
            loading={activity.isLoading}
            error={activity.error}
            errorContext="the activity list"
            emptyMessage="Nothing running."
            rowActions={(a) => (
              <Button size="sm" variant="danger"
                onClick={() => void run("Query cancelled", client.apiFetch(`/postgresql/activity/${encodeURIComponent(String(a.pid))}/cancel`, { method: "POST" }))}>
                Cancel
              </Button>
            )}
          />
        </div>
      </Section>

      <ConfirmDialog
        open={!!newDb}
        onOpenChange={(o) => !o && setNewDb(null)}
        title="New database"
        confirmLabel="Create"
        body={
          <div className="space-y-2">
            <label className="block text-[12px] text-muted">
              Name
              <input value={newDb?.name ?? ""} onChange={(e) => setNewDb(newDb ? { ...newDb, name: e.target.value } : null)}
                className="mt-1 w-full rounded-lg border border-line2 bg-bg px-3 py-1.5 font-mono text-[12.5px] text-fg" />
            </label>
            <label className="block text-[12px] text-muted">
              Owner
              <input value={newDb?.owner ?? ""} placeholder="an existing role" onChange={(e) => setNewDb(newDb ? { ...newDb, owner: e.target.value } : null)}
                className="mt-1 w-full rounded-lg border border-line2 bg-bg px-3 py-1.5 font-mono text-[12.5px] text-fg" />
            </label>
          </div>
        }
        onConfirm={() => {
          const b = newDb;
          setNewDb(null);
          if (!b?.name.trim()) return;
          void run("Database created", client.apiFetch("/postgresql/databases", {
            method: "POST", body: JSON.stringify({ name: b.name.trim(), owner: b.owner.trim() }),
          }));
        }}
      />

      <ConfirmDialog
        open={!!newUser}
        onOpenChange={(o) => !o && setNewUser(null)}
        title={`Add a user to ${newUser?.db ?? ""}`}
        confirmLabel="Create"
        body={
          <div className="space-y-2">
            <label className="block text-[12px] text-muted">
              Username
              <input value={newUser?.username ?? ""} onChange={(e) => setNewUser(newUser ? { ...newUser, username: e.target.value } : null)}
                className="mt-1 w-full rounded-lg border border-line2 bg-bg px-3 py-1.5 font-mono text-[12.5px] text-fg" />
            </label>
            <label className="block text-[12px] text-muted">
              Password
              <input type="password" value={newUser?.password ?? ""} onChange={(e) => setNewUser(newUser ? { ...newUser, password: e.target.value } : null)}
                className="mt-1 w-full rounded-lg border border-line2 bg-bg px-3 py-1.5 font-mono text-[12.5px] text-fg" />
            </label>
            <p className="text-[11.5px] text-muted">Stored by Postgres as a hash; it cannot be read back afterwards.</p>
          </div>
        }
        onConfirm={() => {
          const u = newUser;
          setNewUser(null);
          if (!u?.username.trim()) return;
          void run("User created", client.apiFetch(`/postgresql/databases/${encodeURIComponent(u.db)}/users`, {
            method: "POST", body: JSON.stringify({ username: u.username.trim(), password: u.password }),
          }));
        }}
      />

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.title ?? ""}
        confirmLabel={confirm?.label ?? "Run"}
        destructive
        body={confirm?.body}
        onConfirm={() => {
          const c = confirm;
          setConfirm(null);
          if (c) void run(c.label, c.go());
        }}
      />
    </div>
  );
}
